import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Select, Input } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  ProgressBar,
} from '@/design-system';
import { fetchPrograms } from '@/store/slices/programsSlice';
import { fetchTeams } from '@/store/slices/teamsSlice';
import type { Epic, EpicAllocation } from '@/types';
import type { RootState, AppDispatch } from '@/store';

export interface AllocationManagerProps {
  epic: Epic;
  allocations: EpicAllocation[];
  onCreateAllocation: (allocation: Omit<EpicAllocation, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onUpdateAllocation: (id: string, allocation: Partial<EpicAllocation>) => Promise<void>;
  onDeleteAllocation: (id: string) => Promise<void>;
  isLoading?: boolean;
}

interface AllocationFormData {
  programId: string;
  teamId: string;
  allocatedTeamWeeks: number;
  quarter?: number;
}

export const AllocationManager: React.FC<AllocationManagerProps> = ({
  epic,
  allocations,
  onCreateAllocation,
  onUpdateAllocation,
  onDeleteAllocation,
  isLoading = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { programs } = useSelector((state: RootState) => state.programs);
  const { teams } = useSelector((state: RootState) => state.teams);

  const [isAddingAllocation, setIsAddingAllocation] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState<string | null>(null);
  const [formData, setFormData] = useState<AllocationFormData>({
    programId: '',
    teamId: '',
    allocatedTeamWeeks: 0,
    quarter: undefined,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch programs and teams on mount
  useEffect(() => {
    dispatch(fetchPrograms({ isActive: true }));
    dispatch(fetchTeams({ isActive: true }));
  }, [dispatch]);

  // Calculate allocation summary
  const totalAllocated = allocations.reduce((sum, alloc) => sum + Number(alloc.allocatedTeamWeeks), 0);
  const remaining = epic.requestedTeamWeeks - totalAllocated;
  const percentAllocated = epic.requestedTeamWeeks > 0 
    ? (totalAllocated / epic.requestedTeamWeeks) * 100 
    : 0;

  // Get available teams for selected program
  // Use programId directly or fall back to program.id for compatibility
  const availableTeams = formData.programId
    ? teams.filter(team => (team.programId || team.program?.id) === formData.programId)
    : [];

  const resetForm = () => {
    setFormData({
      programId: '',
      teamId: '',
      allocatedTeamWeeks: 0,
      quarter: undefined,
    });
    setErrors({});
    setIsAddingAllocation(false);
    setEditingAllocation(null);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.programId) {
      newErrors.programId = 'Program is required';
    }

    if (!formData.teamId) {
      newErrors.teamId = 'Team is required';
    }

    if (formData.allocatedTeamWeeks <= 0) {
      newErrors.allocatedTeamWeeks = 'Team weeks must be greater than 0';
    }

    if (formData.allocatedTeamWeeks > 1000) {
      newErrors.allocatedTeamWeeks = 'Team weeks cannot exceed 1000';
    }

    if (formData.quarter && (formData.quarter < 1 || formData.quarter > 4)) {
      newErrors.quarter = 'Quarter must be between 1 and 4';
    }

    // Check for duplicate allocation
    const isDuplicate = allocations.some(alloc => 
      alloc.id !== editingAllocation &&
      alloc.programId === formData.programId &&
      alloc.teamId === formData.teamId &&
      alloc.quarter === formData.quarter
    );

    if (isDuplicate) {
      newErrors.general = 'Allocation already exists for this Program-Team-Quarter combination';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const allocationData = {
        epicId: epic.id,
        programId: formData.programId,
        teamId: formData.teamId,
        allocatedTeamWeeks: formData.allocatedTeamWeeks,
        quarter: formData.quarter,
      };

      if (editingAllocation) {
        await onUpdateAllocation(editingAllocation, allocationData);
      } else {
        await onCreateAllocation(allocationData);
      }

      resetForm();
    } catch (error) {
      console.error('Failed to save allocation:', error);
      setErrors({ general: 'Failed to save allocation. Please try again.' });
    }
  };

  const handleEdit = (allocation: EpicAllocation) => {
    setFormData({
      programId: allocation.programId,
      teamId: allocation.teamId,
      allocatedTeamWeeks: Number(allocation.allocatedTeamWeeks),
      quarter: allocation.quarter || undefined,
    });
    setEditingAllocation(allocation.id);
    setIsAddingAllocation(true);
  };

  const handleDelete = async (allocationId: string) => {
    if (window.confirm('Are you sure you want to delete this allocation?')) {
      try {
        await onDeleteAllocation(allocationId);
      } catch (error) {
        console.error('Failed to delete allocation:', error);
      }
    }
  };

  const handleInputChange = (field: keyof AllocationFormData, value: string | number) => {
    if (field === 'quarter') {
      setFormData(prev => ({ ...prev, [field]: value === '' ? undefined : Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear team selection when program changes
    if (field === 'programId') {
      setFormData(prev => ({ ...prev, teamId: '' }));
    }
    
    // Clear errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const programOptions = programs.map(program => ({
    value: program.id,
    label: program.name,
  }));

  const teamOptions = availableTeams.map(team => ({
    value: team.id,
    label: `${team.name} (${team.memberCount} members)`,
  }));

  const quarterOptions = [
    { value: '', label: 'No specific quarter' },
    { value: '1', label: 'Q1' },
    { value: '2', label: 'Q2' },
    { value: '3', label: 'Q3' },
    { value: '4', label: 'Q4' },
  ];

  return (
    <div className="space-y-4">
      {/* Allocation Summary */}
      <GlassCard variant="subtle" padding="md">
        <h3 className="text-sm font-semibold text-[var(--text)] mb-4">Allocation Summary</h3>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className="text-xl font-bold text-accent-blue">{epic.requestedTeamWeeks}</div>
            <div className="text-xs text-[var(--muted)]">Requested</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-accent-violet">{totalAllocated}</div>
            <div className="text-xs text-[var(--muted)]">Allocated</div>
          </div>
          <div className="text-center">
            <div className={`text-xl font-bold ${remaining >= 0 ? 'text-status-success' : 'text-status-error'}`}>
              {remaining}
            </div>
            <div className="text-xs text-[var(--muted)]">Remaining</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-[var(--muted)]">
            <span>Allocation Progress</span>
            <span>{Math.round(percentAllocated)}%</span>
          </div>
          <ProgressBar
            value={Math.min(percentAllocated, 100)}
            color={percentAllocated > 100 ? 'rose' : percentAllocated >= 80 ? 'emerald' : 'blue'}
            size="md"
          />
        </div>
        {percentAllocated > 100 && (
          <p className="text-xs text-status-error mt-2">
            Over-allocated by {Math.round(percentAllocated - 100)}%
          </p>
        )}
      </GlassCard>

      {/* Add Allocation Button */}
      {!isAddingAllocation && (
        <GlassButton
          onClick={() => setIsAddingAllocation(true)}
          fullWidth
          disabled={isLoading}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Add Allocation
        </GlassButton>
      )}

      {/* Add/Edit Allocation Form */}
      {isAddingAllocation && (
        <GlassCard variant="subtle" padding="md">
          <h4 className="text-sm font-semibold text-[var(--text)] mb-4">
            {editingAllocation ? 'Edit Allocation' : 'Add New Allocation'}
          </h4>

          {errors.general && (
            <div className="mb-4 p-3 rounded-lg border border-status-error/30 bg-status-error/5">
              <p className="text-status-error text-sm">{errors.general}</p>
            </div>
          )}

          {programOptions.length === 0 && (
            <div className="mb-4 p-3 rounded-lg border border-status-warning/30 bg-status-warning/5">
              <p className="text-status-warning text-sm">
                No programs available for allocation. You can only allocate capacity from programs you lead.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Program"
                value={formData.programId}
                onChange={(e) => handleInputChange('programId', e.target.value)}
                options={programOptions}
                placeholder="Select a program..."
                error={errors.programId}
                required
                fullWidth
              />

              <Select
                label="Team"
                value={formData.teamId}
                onChange={(e) => handleInputChange('teamId', e.target.value)}
                options={teamOptions}
                placeholder={!formData.programId ? 'Select a program first' : teamOptions.length === 0 ? 'No teams available' : 'Select a team...'}
                error={errors.teamId}
                required
                fullWidth
                disabled={!formData.programId || teamOptions.length === 0}
                helperText={formData.programId && teamOptions.length === 0 ? 'No teams found for this program' : ''}
              />

              <Input
                label="Team Weeks"
                type="number"
                value={formData.allocatedTeamWeeks.toString()}
                onChange={(e) => handleInputChange('allocatedTeamWeeks', parseFloat(e.target.value) || 0)}
                error={errors.allocatedTeamWeeks}
                required
                fullWidth
                min="0.1"
                max="1000"
                step="0.1"
              />

              <Select
                label="Quarter (Optional)"
                value={formData.quarter?.toString() || ''}
                onChange={(e) => handleInputChange('quarter', e.target.value ? parseInt(e.target.value) : '')}
                options={quarterOptions}
                error={errors.quarter}
                fullWidth
              />
            </div>

            <div className="flex justify-end gap-3">
              <GlassButton
                type="button"
                variant="ghost"
                onClick={resetForm}
                disabled={isLoading}
              >
                Cancel
              </GlassButton>
              <GlassButton
                type="submit"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {editingAllocation ? 'Update' : 'Add'} Allocation
              </GlassButton>
            </div>
          </form>
        </GlassCard>
      )}

      {/* Allocations Table */}
      {allocations.length > 0 && (
        <GlassCard variant="subtle" padding="none" className="overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--border)]">
            <h4 className="text-sm font-semibold text-[var(--text)]">Current Allocations</h4>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--surface-muted)]">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Program</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Team</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Team Weeks</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Quarter</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {allocations.map((allocation) => (
                  <tr key={allocation.id} className="hover:bg-[var(--surface-muted)]/50 transition-colors">
                    <td className="px-4 py-3 text-sm text-[var(--text)]">
                      {allocation.program?.name || 'Unknown Program'}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text)]">
                      {allocation.team?.name || 'Unknown Team'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-accent-violet">
                      {allocation.allocatedTeamWeeks}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--muted)]">
                      {allocation.quarter ? `Q${allocation.quarter}` : 'Any'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleEdit(allocation)}
                          className="p-1.5 rounded-lg text-[var(--muted)] hover:text-accent-blue hover:bg-[var(--surface-muted)] transition-colors"
                          disabled={isLoading}
                          aria-label="Edit allocation"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(allocation.id)}
                          className="p-1.5 rounded-lg text-[var(--muted)] hover:text-status-error hover:bg-status-error/10 transition-colors"
                          disabled={isLoading}
                          aria-label="Delete allocation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}

      {allocations.length === 0 && !isAddingAllocation && (
        <GlassCard variant="subtle" padding="lg" className="text-center">
          <p className="text-sm text-[var(--muted)]">No allocations yet. Add your first allocation to get started.</p>
        </GlassCard>
      )}
    </div>
  );
};

export default AllocationManager;